/**
 * Extracción del mensaje de error del backend para el flujo de Meta Cloud API.
 *
 * `POST /v1/integrations/sync` devuelve los fallos como 4xx con body `{ message }`
 * (p. ej. "No se encontraron numeros de telefono asociados a la cuenta de WhatsApp
 * Business."). Ese texto es lo ÚNICO accionable que recibe el comercio: describe
 * exactamente qué falta en su cuenta de Meta. Antes se descartaba y se sustituía por
 * un genérico, así que un error de configuración de la WABA se veía igual que una
 * caída de red.
 *
 * La ruta `response.data.message` es la misma convención que ya usa el resto del panel
 * (ver `partner/ClaimPlanModal.tsx`), porque `useFetchCallback` re-lanza el error de
 * Axios original preservando `response.data`.
 *
 * Solo se confía en ESA ruta: el `message` de un Error suelto es texto de programador
 * ("Request failed with status code 400", "Facebook SDK not loaded") y mostrarlo sería
 * peor que el genérico en español.
 */

/** Forma mínima del error de Axios re-lanzado por `useFetchCallback`. */
interface ServerErrorShape {
  response?: { data?: { message?: unknown } };
}

/**
 * Devuelve el mensaje del servidor si vino uno usable; si no, el genérico.
 *
 * @param {unknown} error Error capturado en el `catch` del flujo de conexión.
 * @param {string} fallback Texto a usar cuando el servidor no mandó nada legible.
 * @returns {string} Mensaje listo para mostrar al comercio.
 */
export const getServerErrorMessage = (error: unknown, fallback: string): string => {
  const candidate = (error as ServerErrorShape | null | undefined)?.response?.data?.message;
  if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  return fallback;
};
